import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useRef,
  memo,
  useMemo,
  useImperativeHandle,
} from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useSongs } from '../../config/SongContext';
import { useUser } from '../../config/UserContext';
import { useNavigation } from '../../config/NavigationContext';
import { usePostForm } from '../../config/PostFormContext';
import { db } from '../../config/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import NavBar from './NavBar';
import SongInfo from './SongInfo';
import PlayerSection from './PlayerSection';
import DescriptionSection from './DescriptionSection';
import CommentForm from '../CommentForm/CommentForm';
import CommentSection from './CommentSection';
import LikersModal from './LikersModal';
import SongPageMeta from './SongPageMeta';
import SongPageLoadingShell from './SongPageLoadingShell';
import HeartLoader from '../HeartLoader/HeartLoader';
import styles from './SongPage.module.scss';

import Edit from '../Icons/Edit';
import YouTube from '../Icons/YouTube';
import TrashCan from '../Icons/TrashCan';
import Share from '../Icons/Share';
import { useOpenGraph } from '../../hooks/useOpenGraph';
import logger from '../../utils/logger';
import { useShare } from '../../config/ShareContext';
import ScrollContainer from '../ScrollContainer/ScrollContainer';

import { renderDescriptionWithLinks } from './utils/renderDescriptionWithLinks';
import useIsMobile from './hooks/useIsMobile';
import useScrollRevealNavbar from './hooks/useScrollRevealNavbar';
import useScrollToTarget from './hooks/useScrollToTarget';
import useLikersDetails from './hooks/useLikersDetails';
import useSongLikeActions from './hooks/useSongLikeActions';
import useSongCommentActions from './hooks/useSongCommentActions';
import { useSongCommentLikeActions } from './hooks/useSongCommentLikeActions';
import useSongEmojiActions from './hooks/useSongEmojiActions';
import useSongRecommendActions from './hooks/useSongRecommendActions';
import { useSongDetailsListener } from './hooks/useSongDetailsListener';
import { normalizeCommentsWithIds } from '../../utils/ensureCommentIds';

/** Matches `songColumnTransition` (non–reduced-motion) and `handleUnmount` delay. */
const SONG_COLUMN_FADE_MS = 220;

const SongPage = memo(
  forwardRef(
    (
      {
        song,
        onClose,
        onMinimize,
        openSongPage: propOpenSongPage,
        isSongLoading = false,
        targetCommentId = null,
        targetReplyId = null,
        standalone = false,
        onPlayStateChange,
      },
      ref
    ) => {
      const {
        handleUserPageNav,
        handleUserPageNavByHandle,
        openSongPage: contextOpenSongPage,
      } = useNavigation();
      const { openEditModal } = usePostForm();
      const { share } = useShare();

      const openSongPage = propOpenSongPage || contextOpenSongPage;
      const [isLoading, setIsLoading] = useState(true);
      const [loadingProgress, setLoadingProgress] = useState(0);
      const [songDetails, setSongDetails] = useState(song);
      const [userDetails, setUserDetails] = useState(null);
      const [showConfirmDialog, setShowConfirmDialog] = useState(false);
      const [showLikers, setShowLikers] = useState(false);
      const [fetchWarnings, setFetchWarnings] = useState({
        song: false,
        user: false,
      });
      const isTransitioning = useRef(false);

      if (song.id !== songDetails.id) {
        setSongDetails(song);
        setUserDetails(null);
        isTransitioning.current = false;
        setFetchWarnings({ song: false, user: false });
      }

      const mountedRef = useRef(false);
      const isInitializedRef = useRef(false);
      const playerRef = useRef(null);
      const playerSectionRef = useRef(null);

      useImperativeHandle(ref, () => ({
        play: () => playerRef.current?.play(),
        pause: () => playerRef.current?.pause(),
        isPlaying: () => playerRef.current?.isPlaying?.() ?? false,
        getProgress: () => playerRef.current?.getProgress?.() ?? 0,
      }));

      const commentFormRef = useRef(null);
      const songInfoRef = useRef(null);
      const scrollContainerRef = useRef(null);

      const isMobile = useIsMobile();
      const prefersReducedMotion = useReducedMotion();
      const songColumnTransition = prefersReducedMotion
        ? { duration: 0 }
        : {
            duration: SONG_COLUMN_FADE_MS / 1000,
            ease: [0.4, 0, 0.2, 1],
          };

      const { getNextSong, setCurrentSong, deleteSong } = useSongs();
      const { currentUser, getUserDetailsById } = useUser();
      const {
        getSongById,
        likeSong,
        unlikeSong,
        addComment,
        deleteComment,
        addReply,
        addEmojiReaction,
        removeEmojiReaction,
        likeComment,
        likeReply,
        unlikeComment,
        unlikeReply,
      } = useSongs();
      const [isLoadingNextSong, setIsLoadingNextSong] = useState(false);
      const recommendationsRef = useRef(null);
      const commentSectionRef = useRef(null);
      const backfilledCommentIdsRef = useRef(new Set());
      const canonicalSongUrl = `https://goodsong.club/song/${songDetails?.id || song.id}`;

      const { openGraphData, isLoading: openGraphLoading } = useOpenGraph(
        songDetails?.description
      );

      const {
        handleAddComment,
        handleDeleteComment,
        handleAddReply,
        handleDeleteReply,
      } = useSongCommentActions({
        songDetails,
        setSongDetails,
        currentUser,
        addComment,
        deleteComment,
        addReply,
        getSongById,
      });

      const {
        handleLikeComment,
        handleUnlikeComment,
        handleLikeReply,
        handleUnlikeReply,
      } = useSongCommentLikeActions({
        setSongDetails,
        likeComment,
        unlikeComment,
        likeReply,
        unlikeReply,
      });

      useEffect(() => {
        if (!songDetails?.id || !Array.isArray(songDetails.comments)) return;
        if (backfilledCommentIdsRef.current.has(songDetails.id)) return;

        const { comments, changed } = normalizeCommentsWithIds(songDetails.comments);
        if (!changed) {
          backfilledCommentIdsRef.current.add(songDetails.id);
          return;
        }

        // Apply ids locally first so likes can resolve commentId/replyId before
        // the Firestore backfill write completes (notification callables need one).
        setSongDetails((prev) =>
          prev.id === songDetails.id ? { ...prev, comments } : prev
        );

        let cancelled = false;
        (async () => {
          try {
            await updateDoc(doc(db, 'songs', songDetails.id), { comments });
            if (!cancelled) {
              backfilledCommentIdsRef.current.add(songDetails.id);
            }
          } catch (err) {
            logger.warn('[SongPage] Failed to backfill legacy comment ids', err);
          }
        })();

        return () => {
          cancelled = true;
        };
      }, [songDetails?.id, songDetails?.comments]);

      const { isLiked, isLiking, likesAmount, handleLike, handleUnlike } =
        useSongLikeActions({
          songDetails,
          setSongDetails,
          currentUser,
          likeSong,
          unlikeSong,
        });

      const {
        handleEmojiSelect,
        displayUserEmoji,
        displayEmojiReactions,
      } = useSongEmojiActions({
        songDetails,
        setSongDetails,
        currentUser,
        addEmojiReaction,
        removeEmojiReaction,
        getSongById,
      });

      const { handleRecommend } = useSongRecommendActions({
        songDetails,
        setSongDetails,
        currentUser,
      });

      const {
        likersDetails,
        isLoading: likersLoading,
        hasError: likersHasError,
        retry: retryLikers,
      } = useLikersDetails(songDetails?.likes, getUserDetailsById);

      useEffect(() => {
        mountedRef.current = true;

        return () => {
          mountedRef.current = false;
          // eslint-disable-next-line react-hooks/exhaustive-deps -- destroy player instance via ref at teardown
          const currentPlayer = playerRef.current;
          if (currentPlayer?.destroy) {
            currentPlayer.destroy();
          }
        };
      }, [song.id]);

      useEffect(() => {
        if (!isInitializedRef.current) {
          isInitializedRef.current = true;

          return;
        }

        return () => {
          logger.debug('SongPage cleanup');
          // eslint-disable-next-line react-hooks/exhaustive-deps -- destroy player instance via ref at teardown
          const currentPlayer = playerRef.current;
          if (currentPlayer && currentPlayer.destroy) {
            currentPlayer.destroy();
          }
        };
      }, []);

      const handleUnmount = useCallback(() => {
        if (typeof onClose !== 'function') return;
        const delay = prefersReducedMotion ? 0 : SONG_COLUMN_FADE_MS;
        setTimeout(() => {
          onClose();
        }, delay);
      }, [onClose, prefersReducedMotion]);

      /*
        Forward vertical wheel to the inner ScrollContainer viewport when the
        cursor is over a focusable/interactive child that would otherwise
        swallow the event. Only relevant in the multi-panel layout where this
        component owns its own scroll surface — in standalone (shared-link)
        mode an ancestor owns scrolling, so we install no-op on the disabled
        container and let the native scroll chain reach the outer scroller
        unmolested. Attaching a handler there would make React install a
        non-passive root wheel listener that Chrome can treat as scroll-
        blocking over the ScrollContainer's subtree (symptom: cursor over a
        comment freezes the outer scroll).
      */
      const handleWheelCapture = useCallback(
        (e) => {
          if (standalone) return;
          if (!scrollContainerRef.current) return;
          const isVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
          if (isVertical) {
            scrollContainerRef.current.scrollTop += e.deltaY;
          }
        },
        [standalone]
      );

      const handleUserPageNavFromSong = useCallback(
        (userId) => {
          if (isMobile) {
            handleUnmount();
          }
          handleUserPageNav(userId);
        },
        [isMobile, handleUnmount, handleUserPageNav]
      );

      const navbarState = useScrollRevealNavbar({
        ref: scrollContainerRef,
        isReady: !isLoading,
        resetKey: song.id,
      });

      const scrollToCommentSection = () => {
        if (commentSectionRef.current) {
          logger.debug('Scrolling to comment section');
          commentSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        } else {
          logger.debug('Comment section ref is null');
        }
      };

      const scrollToRecommendations = () => {
        if (recommendationsRef.current) {
          logger.debug('Scrolling to recommendations');
          recommendationsRef.current.scrollIntoView({ behavior: 'smooth' });
        } else {
          logger.debug('Recommendations ref is null');
        }
      };

      const commentCount = useMemo(
        () => songDetails?.comments?.length || 0,
        [songDetails]
      );

      const handleSongListenerError = useCallback(() => {
        setFetchWarnings((w) => ({ ...w, song: true }));
      }, []);

      useSongDetailsListener(song.id, setSongDetails, {
        onError: handleSongListenerError,
      });

      useEffect(() => {
        if (!songDetails?.userId) return;
        const fetchUserDetails = async () => {
          try {
            const data = await getUserDetailsById(songDetails.userId);
            setUserDetails(data);
          } catch (error) {
            logger.error('[SongPage] Failed to load user details', error);
            setFetchWarnings((w) => ({ ...w, user: true }));
            setUserDetails(null);
          }
        };
        fetchUserDetails();
      }, [songDetails?.userId, getUserDetailsById]);

      const handleDelete = useCallback(() => {
        setShowConfirmDialog(true);
      }, []);

      const confirmDelete = async () => {
        try {
          await deleteSong(songDetails.id);
          logger.debug(`Song with ID ${songDetails.id} deleted successfully.`);
          setShowConfirmDialog(false);
          onClose(); // Close the SongPage after deletion
        } catch (error) {
          logger.error('Error deleting song:', error);
        }
      };

      const isCurrentUser =
        songDetails && currentUser && songDetails.userId === currentUser.uid;

      const handleDescriptionMentionClick = useCallback(
        (e) => {
          const el = e.target.closest('[data-mention-handle]');
          if (el) {
            e.preventDefault();
            handleUserPageNavByHandle(el.dataset.mentionHandle);
          }
        },
        [handleUserPageNavByHandle]
      );

      const handleShare = useCallback(() => {
        share(songDetails);
      }, [share, songDetails]);

      const shareMenuOption = useMemo(
        () => ({
          label: 'Share',
          handler: handleShare,
          icon: <Share />,
        }),
        [handleShare]
      );

      const menuOptions1 = useMemo(
        () => [
          shareMenuOption,
          {
            label: 'Watch on YouTube',
            handler: () => window.open(songDetails.URL),
            icon: <YouTube />,
          },
        ],
        [shareMenuOption, songDetails.URL]
      );

      const menuOptions2 = useMemo(
        () => [
          shareMenuOption,
          {
            label: 'Watch on YouTube',
            handler: () => window.open(songDetails.URL),
            icon: <YouTube />,
          },
          {
            label: 'Edit',
            handler: () => openEditModal(songDetails),
            icon: <Edit />,
          },
          {
            label: 'Delete',
            handler: handleDelete,
            icon: <TrashCan />,
            isDestructive: true,
          },
        ],
        [shareMenuOption, songDetails, openEditModal, handleDelete]
      );

      const handleSongEnd = useCallback(async () => {
        if (isSongLoading) {
          logger.debug('[SongPage] Song load already in progress, skipping');
          return;
        }

        logger.debug('[SongPage] Handling song end for:', song.id);
        setIsLoadingNextSong(true);

        try {
          const nextSong = await getNextSong(song.id);
          logger.debug('[SongPage] Next song found:', nextSong?.id);

          if (nextSong) {
            setCurrentSong(nextSong);
            setTimeout(() => {
              logger.debug('[SongPage] Triggering navigation to next song');
              openSongPage(nextSong.id);
            }, 0);
          } else {
            logger.debug('[SongPage] No next song available');
          }
        } catch (error) {
          logger.error('[SongPage] Error during song transition:', error);
        } finally {
          setIsLoadingNextSong(false);
        }
      }, [song.id, getNextSong, setCurrentSong, openSongPage, isSongLoading]);

      useEffect(() => {
        const loadData = async () => {
          if (!song.id) return;

          setIsLoading(true);
          setLoadingProgress(0);

          try {
            if (song.id !== songDetails?.id) {
              setSongDetails(song); // Set initial data immediately
              setLoadingProgress(50);

              if (
                song.userId &&
                (!userDetails || userDetails.uid !== song.userId)
              ) {
                try {
                  const userData = await getUserDetailsById(song.userId);
                  setUserDetails(userData);
                } catch (userError) {
                  logger.error(
                    '[SongPage] Failed to load user details',
                    userError
                  );
                  setUserDetails(null);
                  setFetchWarnings((w) => ({ ...w, user: true }));
                }
                setLoadingProgress(100);
              } else {
                setLoadingProgress(100);
              }
            } else {
              setLoadingProgress(100);
            }
          } catch (error) {
            logger.error('[SongPage] Error loading data:', error);
          } finally {
            setIsLoading(false);
          }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- expand deps would retrigger load loops; song.id drives refresh
      }, [song.id, song.userId, getUserDetailsById]);

      useScrollToTarget({ isLoading, targetCommentId, targetReplyId });

      if (isLoading) {
        return <SongPageLoadingShell progress={loadingProgress} />;
      }

      if (isLoadingNextSong || isSongLoading) {
        const loadingMessage = isLoadingNextSong
          ? 'Loading next song...'
          : 'Loading random song...';

        return (
          <SongPageLoadingShell
            progress={loadingProgress || 100}
            message={loadingMessage}
          />
        );
      }

      return (
        <div className={styles.songPage}>
          {isTransitioning.current && (
            <motion.div
              className={styles.loadingOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <SongPageLoadingShell
                progress={loadingProgress}
                message="Loading..."
              />
            </motion.div>
          )}
          <SongPageMeta
            songDetails={songDetails}
            canonicalSongUrl={canonicalSongUrl}
          />

          {songDetails ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={song.id}
                className={styles.songPageContainer}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={songColumnTransition}
              >
                {!standalone && (
                  <NavBar
                    songDetails={songDetails}
                    navbarState={navbarState}
                    onClose={handleUnmount}
                    onSkip={handleSongEnd}
                    onMinimize={onMinimize}
                    isLoadingNextSong={isLoadingNextSong || isSongLoading}
                  />
                )}
                {(fetchWarnings.song || fetchWarnings.user) && (
                  <div className={styles.fetchWarning} role="status">
                    <p className={styles.fetchWarningText}>
                      {fetchWarnings.song &&
                        "Couldn't refresh the latest song details."}
                      {fetchWarnings.song && fetchWarnings.user && ' '}
                      {fetchWarnings.user &&
                        "Couldn't load the poster's profile."}
                    </p>
                  </div>
                )}
                {/*
                  In standalone mode (shared-link layout) SongPage is rendered
                  inside an outer scroll surface that owns the whole page, so
                  we disable the inner scroller. Leaving it on would swallow
                  wheel events (overscroll-behavior: contain on
                  .postBodyContainer) and the outer page would feel dead
                  whenever the cursor sat over the 640px content column.
                */}
                <ScrollContainer
                  ref={scrollContainerRef}
                  className={
                    standalone
                      ? styles.scrollRegionMount
                      : `${styles.postBodyContainer} ${styles.scrollRegionMount}`
                  }
                  onWheelCapture={standalone ? undefined : handleWheelCapture}
                  disabled={standalone}
                >
                  <div className={styles.postBody}>
                    <SongInfo
                      songDetails={songDetails}
                      userDetails={userDetails}
                      handleUserPageNavFromSong={handleUserPageNavFromSong}
                      songInfoRef={songInfoRef}
                    />

                    <div className={styles.container}>
                      <PlayerSection
                        songDetails={songDetails}
                        handleSongEnd={handleSongEnd}
                        playerRef={playerRef}
                        sectionRef={playerSectionRef}
                        onPlayStateChange={onPlayStateChange}
                      />

                      <DescriptionSection
                        songDetails={songDetails}
                        likesAmount={likesAmount}
                        commentCount={commentCount}
                        recommendationCount={
                          songDetails.recommendations?.length || 0
                        }
                        isLiked={isLiked}
                        handleLike={handleLike}
                        handleUnlike={handleUnlike}
                        isLiking={isLiking}
                        isCurrentUser={isCurrentUser}
                        menuOptions1={menuOptions1}
                        menuOptions2={menuOptions2}
                        setShowLikers={setShowLikers}
                        scrollToCommentSection={scrollToCommentSection}
                        scrollToRecommendations={scrollToRecommendations}
                        renderDescriptionWithLinks={(text) =>
                          renderDescriptionWithLinks(
                            text,
                            songDetails.mentionedHandles
                          )
                        }
                        onDescriptionMentionClick={
                          handleDescriptionMentionClick
                        }
                        handleRecommend={handleRecommend}
                        handleEmojiSelect={handleEmojiSelect}
                        emojiReactions={displayEmojiReactions}
                        currentUserEmoji={displayUserEmoji}
                        openGraphData={openGraphData}
                        openGraphLoading={openGraphLoading}
                      />
                      {currentUser && (
                        <CommentForm
                          ref={commentFormRef}
                          currentUser={currentUser}
                          addComment={handleAddComment}
                          songId={songDetails.id}
                          placeholder="Leave a comment"
                        />
                      )}
                    </div>
                    <CommentSection
                      songDetails={songDetails}
                      setSongDetails={setSongDetails}
                      currentUser={currentUser}
                      handleAddComment={handleAddComment}
                      handleDeleteComment={handleDeleteComment}
                      handleDeleteReply={handleDeleteReply}
                      handleAddReply={handleAddReply}
                      handleLikeComment={handleLikeComment}
                      handleUnlikeComment={handleUnlikeComment}
                      handleLikeReply={handleLikeReply}
                      handleUnlikeReply={handleUnlikeReply}
                      showConfirmDialog={showConfirmDialog}
                      confirmDelete={confirmDelete}
                      setShowConfirmDialog={setShowConfirmDialog}
                      commentFormRef={commentFormRef}
                      handleUserPageNavFromSong={handleUserPageNavFromSong}
                      db={db}
                      recommendationsRef={recommendationsRef}
                      commentSectionRef={commentSectionRef}
                      highlightCommentId={
                        targetReplyId ? null : targetCommentId
                      }
                      highlightReplyId={targetReplyId}
                    />
                  </div>
                </ScrollContainer>
                <LikersModal
                  showLikers={showLikers}
                  setShowLikers={setShowLikers}
                  likersDetails={likersDetails}
                  isLoading={likersLoading}
                  hasError={likersHasError}
                  onRetry={retryLikers}
                  handleUserPageNavFromSong={handleUserPageNavFromSong}
                />
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className={styles.loadingContainer}>
              <HeartLoader progress={loadingProgress} noRise={true} />
              <p className={styles.loadingText}>Loading...</p>
            </div>
          )}
        </div>
      );
    }
  ),
  (prevProps, nextProps) => {
    // Only re-render if essential props change
    return (
      prevProps.song.id === nextProps.song.id &&
      prevProps.onClose === nextProps.onClose &&
      prevProps.onMinimize === nextProps.onMinimize &&
      prevProps.openSongPage === nextProps.openSongPage &&
      prevProps.isSongLoading === nextProps.isSongLoading &&
      prevProps.targetCommentId === nextProps.targetCommentId &&
      prevProps.targetReplyId === nextProps.targetReplyId &&
      prevProps.standalone === nextProps.standalone
    );
  }
);

export default SongPage;
